"use client"

import { motion } from "framer-motion"
import { useEffect, useMemo, useState } from "react"
import { flushSync } from "react-dom"
import { FiHelpCircle, FiInbox } from "react-icons/fi"
import { RecommendationModal } from "@/components/resources/recommendation-modal"
import { ResourceColumn } from "@/components/resources/resource-column"
import { ResourcesHeader } from "@/components/resources/resources-header"
import { filterResources } from "@/lib/resource-filters"
import { RESOURCE_CATEGORIES } from "@/lib/resources-data"
import type { ResourceModelContext } from "@/lib/resources-webmcp"
import { registerResourceFilterTool } from "@/lib/resources-webmcp"

function useColumnCount() {
    const [cols, setCols] = useState(4)

    useEffect(() => {
        const update = () => {
            if (window.matchMedia("(min-width: 1280px)").matches) {
                setCols(4)
            } else if (window.matchMedia("(min-width: 1024px)").matches) {
                setCols(3)
            } else if (window.matchMedia("(min-width: 768px)").matches) {
                setCols(2)
            } else {
                setCols(1)
            }
        }
        update()
        window.addEventListener("resize", update, { passive: true })
        return () => window.removeEventListener("resize", update)
    }, [])

    return cols
}

export function ResourcesView() {
    const [searchQuery, setSearchQuery] = useState("")
    const [activeCategory, setActiveCategory] = useState("all")
    const [dailyOnly, setDailyOnly] = useState(false)
    const [isRequestModalOpen, setIsRequestModalOpen] = useState(false)
    const colCount = useColumnCount()

    // Compute total resources count
    const totalCount = useMemo(() => {
        return RESOURCE_CATEGORIES.reduce(
            (acc, cat) => acc + cat.items.length,
            0
        )
    }, [])

    // Category pills data
    const categoryPills = useMemo(() => {
        return RESOURCE_CATEGORIES.map((cat) => ({
            id: cat.id,
            title: cat.title,
            count: cat.items.length,
        }))
    }, [])

    useEffect(() => {
        const context =
            (
                document as Document & {
                    modelContext?: ResourceModelContext
                }
            ).modelContext ??
            (
                navigator as Navigator & {
                    modelContext?: ResourceModelContext
                }
            ).modelContext
        return registerResourceFilterTool(context, (filters) => {
            flushSync(() => {
                setSearchQuery(filters.query)
                setActiveCategory(filters.category)
                setDailyOnly(filters.dailyOnly)
            })
        })
    }, [])

    const filteredCategories = useMemo(
        () =>
            filterResources({
                query: searchQuery,
                category: activeCategory,
                dailyOnly,
            }),
        [searchQuery, activeCategory, dailyOnly]
    )

    const totalMatchingItems = useMemo(() => {
        return filteredCategories.reduce(
            (acc, cat) => acc + cat.items.length,
            0
        )
    }, [filteredCategories])

    // Seed each column in source order, then place remaining categories in the
    // lightest column. Item count closely tracks the rendered card height.
    const columns = useMemo(() => {
        if (filteredCategories.length === 0) return []

        const effectiveCols = Math.min(colCount, filteredCategories.length)
        const colsArray: {
            category: (typeof filteredCategories)[0]
            originalIndex: number
        }[][] = Array.from({ length: effectiveCols }, () => [])
        const columnWeights = filteredCategories
            .slice(0, effectiveCols)
            .map((category) => category.items.length + 2)
        const remainingCategories = filteredCategories.slice(effectiveCols)
        const assignments = Array.from(
            { length: remainingCategories.length },
            () => 0
        )
        let bestAssignments = [...assignments]
        let bestSpread = Number.POSITIVE_INFINITY

        const findBestBalance = (categoryIndex: number) => {
            if (categoryIndex === remainingCategories.length) {
                const spread =
                    Math.max(...columnWeights) - Math.min(...columnWeights)
                if (spread < bestSpread) {
                    bestSpread = spread
                    bestAssignments = [...assignments]
                }
                return
            }

            const categoryWeight =
                remainingCategories[categoryIndex].items.length + 2
            for (
                let columnIndex = 0;
                columnIndex < effectiveCols;
                columnIndex++
            ) {
                assignments[categoryIndex] = columnIndex
                columnWeights[columnIndex] += categoryWeight
                findBestBalance(categoryIndex + 1)
                columnWeights[columnIndex] -= categoryWeight
            }
        }

        findBestBalance(0)

        filteredCategories.forEach((category, index) => {
            const columnIndex =
                index < effectiveCols
                    ? index
                    : bestAssignments[index - effectiveCols]
            colsArray[columnIndex].push({
                category,
                originalIndex: index,
            })
        })

        return colsArray
    }, [filteredCategories, colCount])

    return (
        <div className="container mx-auto flex flex-col gap-10 px-4 py-12 sm:py-16">
            <ResourcesHeader
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                activeCategory={activeCategory}
                onCategoryChange={setActiveCategory}
                categories={categoryPills}
                totalCount={totalCount}
                dailyOnly={dailyOnly}
                onToggleDailyOnly={() => setDailyOnly((prev) => !prev)}
                onOpenRequestModal={() => setIsRequestModalOpen(true)}
            />

            {/* Results Counter / Filter Feedback */}
            {(searchQuery || activeCategory !== "all" || dailyOnly) && (
                <div className="flex flex-wrap items-center justify-between gap-2 font-mono text-muted-foreground text-xs">
                    <span className="min-w-0">
                        Showing {totalMatchingItems} of {totalCount} resources
                        {dailyOnly && " (Everyday stack only)"}
                        {searchQuery && ` matching "${searchQuery}"`}
                    </span>
                    <button
                        type="button"
                        onClick={() => {
                            setSearchQuery("")
                            setActiveCategory("all")
                            setDailyOnly(false)
                        }}
                        className="shrink-0 underline hover:text-foreground"
                    >
                        Reset filters
                    </button>
                </div>
            )}

            {/* Main Multi-Column Masonry Flow (no artificial row gaps) */}
            {columns.length > 0 ? (
                <div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {columns.map((columnGroup) => (
                        <div
                            key={`column-${columnGroup[0]?.category.id}`}
                            className="flex flex-col gap-6"
                        >
                            {columnGroup.map(({ category, originalIndex }) => (
                                <motion.div
                                    key={category.id}
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{
                                        duration: 0.35,
                                        delay: Math.min(
                                            originalIndex * 0.05,
                                            0.3
                                        ),
                                    }}
                                >
                                    <ResourceColumn
                                        category={category}
                                        index={originalIndex}
                                    />
                                </motion.div>
                            ))}
                        </div>
                    ))}
                </div>
            ) : (
                <div className="flex flex-col items-center justify-center border border-border/70 border-dashed px-4 py-16 text-center sm:px-6 sm:py-20 dark:border-border/40">
                    <FiInbox className="mb-3 size-8 text-muted-foreground/50" />
                    <p className="font-medium text-foreground text-sm">
                        No resources match your filters
                    </p>
                    <p className="mt-1 max-w-md text-muted-foreground text-xs leading-relaxed">
                        Can&apos;t find what you&apos;re looking for? Try
                        clearing your search or submit a request for an app
                        recommendation.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                        <button
                            type="button"
                            onClick={() => {
                                setSearchQuery("")
                                setActiveCategory("all")
                                setDailyOnly(false)
                            }}
                            className="border border-border/70 bg-muted/40 px-3 py-1.5 font-mono text-xs uppercase tracking-wider hover:bg-muted hover:text-foreground dark:border-border/40"
                        >
                            Clear all filters
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsRequestModalOpen(true)}
                            className="inline-flex items-center gap-1.5 border border-foreground bg-foreground px-3 py-1.5 font-mono text-background text-xs uppercase tracking-wider transition-colors hover:bg-foreground/90"
                        >
                            <FiHelpCircle className="size-3.5" />
                            <span>Ask for a recommendation</span>
                        </button>
                    </div>
                </div>
            )}

            {/* Tool Recommendation Request Modal */}
            <RecommendationModal
                isOpen={isRequestModalOpen}
                onClose={() => setIsRequestModalOpen(false)}
                initialQuery={searchQuery}
            />
        </div>
    )
}
